import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { usePermissions } from "./PermissionContext";
import { apiClient } from "../utils/const"; // Axios instance

const Permission = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { updatePermissions } = usePermissions();

  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState({
    customer: false,
    payment: false,
    report: false,
    employee: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        // Fetch user data - try permissions endpoint first since it has getUserById action
        const userRes = await apiClient.post("/permissions.php", {
          action: "getUserById",
          userId: id, // Use userId to match backend expectations
        });

        if (userRes.data.message === 'radha') {
          // Handle different possible response structures
          let userData = null;
          if (userRes.data.user) {
            userData = userRes.data.user;
          } else if (userRes.data.data && userRes.data.data.user) {
            userData = userRes.data.data.user;
          } else if (userRes.data.data) {
            userData = userRes.data.data;
          }

          setUser(userData);
        } else {
          toast.error( "Failed to load user");
        }

        // Fetch permissions
        const permRes = await apiClient.post("/permissions.php", {
          action: "getPermissions",
          userId: id, // Use userId to match backend expectations
        });
        if (permRes.data.message === 'radha' ) {
          setPermissions(permRes.data.data.permissions);
        }

        setLoading(false);
      } catch (err) {
        toast.error(err.message || "Network error while loading data.");
        setLoading(false);
      }
    };

    if (id) {
      fetchUserData();
    } else {
      setError("No user ID provided");
      setLoading(false);
    }
  }, [id]);

  const handleChange = (perm) => {
    setPermissions((prev) => ({
      ...prev,
      [perm]: !prev[perm],
    }));
  };

  const handleSave = async () => {
    if (!user) {
      toast.error("User data not loaded");
      return;
    }

    setSaving(true);
    try {
      // Update user data - use adminuser.php since permissions.php doesn't have updateUser
      const updateRes = await apiClient.post("/adminuser.php", {
        action: "updateUser",
        userId: id, // matches $data['userId']
        userData: {
          // matches $data['userData']
          name: user.name,
          phone: user.phone,
          email: user.email,
          gender: user.gender, // optional if backend supports extra fields
          address: user.address, // optional
        },
      });

      if (!updateRes.data.success) {
        throw new Error(updateRes.data.message || "User update failed.");
      }

      // Update permissions using permissions.php
      const permRes = await apiClient.post("/permissions.php", {
        action: "savePermissions",
        userId: id, // Use userId to match backend expectations
        permissions,
      });

      if (!permRes.data.success) {
        throw new Error(permRes.data.message || "Failed to save permissions.");
      }

      // Update local permissions if editing current user
      const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
      if (currentUser?.id === id) {
        updatePermissions(permissions);
      }

      toast.success("User and permissions updated successfully!");
      setTimeout(() => navigate("/EmployeeList"), 1500);
    } catch (err) {
      console.error("Save error:", err);
      toast.error(err.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="page-content">
          <div className="container-xxl">
            <div className="text-center p-4">Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-wrapper">
        <div className="page-content">
          <div className="container-xxl">
            <div className="alert alert-danger m-3">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="page-wrapper">
        <div className="page-content">
          <div className="container-xxl">
            <div className="row">
              <div className="col-12">
                <div className="card">
                  <div className="card-header">
                    <h4 className="card-title">Edit Employee</h4>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      {/* User Info */}
                      <div className="col-md-5">
                        <h5>User Details</h5>
                        <div className="mb-3">
                          <label className="form-label">Full Name</label>
                          <input
                            type="text"
                            className="form-control"
                            value={user?.name || ""}
                            onChange={(e) =>
                              setUser((prev) => ({
                                ...prev,
                                name: e.target.value,
                              }))
                            }
                          />
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Mobile Number</label>
                          <input
                            type="text"
                            className="form-control"
                            value={user?.phone || ""}
                            onChange={(e) =>
                              setUser((prev) => ({
                                ...prev,
                                phone: e.target.value,
                              }))
                            }
                          />
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Email</label>
                          <input
                            type="email"
                            className="form-control"
                            value={user?.email || ""}
                            onChange={(e) =>
                              setUser((prev) => ({
                                ...prev,
                                email: e.target.value,
                              }))
                            }
                          />
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Gender</label>
                          <select
                            className="form-control"
                            value={user?.gender || ""}
                            onChange={(e) =>
                              setUser((prev) => ({
                                ...prev,
                                gender: e.target.value,
                              }))
                            }
                          >
                            <option value="">Select Gender</option>
                            <option value="M">Male</option>
                            <option value="F">Female</option>
                            <option value="O">Other</option>
                          </select>
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Address</label>
                          <textarea
                            className="form-control"
                            value={user?.address || ""}
                            onChange={(e) =>
                              setUser((prev) => ({
                                ...prev,
                                address: e.target.value,
                              }))
                            }
                            rows="3"
                          />
                        </div>
                      </div>

                      {/* Divider */}
                      <div className="col-md-1 d-flex justify-content-center">
                        <div className="border-start h-100" />
                      </div>

                      {/* Permissions */}
                      <div className="col-md-6">
                        <h5>Permissions</h5>
                        <div className="mb-3">
                          <p className="text-muted">
                            Select the modules this employee can access:
                          </p>
                        </div>
                        {["customer", "payment", "report", "employee"].map(
                          (perm) => (
                            <div className="form-check mb-3" key={perm}>
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={perm}
                                checked={permissions[perm]}
                                onChange={() => handleChange(perm)}
                              />
                              <label
                                className="form-check-label"
                                htmlFor={perm}
                              >
                                <strong>
                                  {perm.charAt(0).toUpperCase() + perm.slice(1)}{" "}
                                  Management
                                </strong>
                                <br />
                                <small className="text-muted">
                                  {perm === "customer" &&
                                    "Access to member records and management"}
                                  {perm === "payment" &&
                                    "Access to payment processing and records"}
                                  {perm === "report" &&
                                    "Access to reports and analytics"}
                                  {perm === "employee" &&
                                    "Access to employee management"}
                                </small>
                              </label>
                            </div>
                          )
                        )}
                        <div className="mt-4 d-flex gap-2">
                          <button
                            className="btn btn-secondary"
                            onClick={() => navigate("/EmployeeList")}
                          >
                            Cancel
                          </button>
                          <button
                            className="btn btn-primary"
                            onClick={handleSave}
                            disabled={saving}
                            style={{
                              backgroundColor: "#4B2C3B",
                              borderColor: "#4B2C3B",
                            }}
                          >
                            {saving ? "Saving..." : "Save Changes"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Permission;
